import { GridFilterItem } from '@mui/x-data-grid-pro';

import { buildFilterModel, filterModelToFilterState } from './serviceListFilters';
import { parseAdvancedFilters, serializeAdvancedFilter } from './serviceListUrlStateHelpers';
import { FilterState } from '../../components/molecules/FilterBar';
import {
  parseNumberParam,
  parseSortParam,
  parseStringListParam,
  setOptionalParam,
  setSortParam,
  setStringListParam,
  SortToken,
} from '../../utils/listUrlState';
import { getServiceStatusNamesByStatusGroup } from '../../utils/services';

/**
 * URL keys the services-list codec writes. Cleared before re-emit so the
 * URL is the single canonical representation of filter state. Anything
 * outside this list (`locationId`, `importSessionId`, `structureNodeId`,
 * `savedReportCode`, the path-derived default report) scopes the BFF query
 * independently and is left untouched.
 */
const OWNED_KEYS = [
  'multiSearchQuery',
  'serviceStatusName',
  'serviceTypeName',
  'contractName',
  'clientName',
  'clientCode',
  'contractLegacy',
  'serviceId',
  'af',
  'sort',
  'page',
  'pageSize',
] as const;

/**
 * One-shot legacy keys we delete after first hydration so the URL
 * stabilises into the canonical chip form. `serviceStatusGroup` is the
 * activity-monitor entry point — once expanded into explicit
 * `serviceStatusName` chips it becomes redundant. `savedReportCode` is
 * resolved by `useReports` on first load and would otherwise re-apply on
 * every back-button remount on top of the chip state.
 */
const TRANSIENT_KEYS = ['serviceStatusGroup', 'savedReportCode'] as const;

/** Upper bound on `pageSize` accepted from the URL. Higher values are
 *  clamped — guards against accidental or malicious inputs that would
 *  push a huge query through the BFF or fragment the React Query cache. */
const MAX_PAGE_SIZE = 200;

/** Chip-style URL keys whose presence flags the codec as the source of
 *  truth for the initial filter model (rather than the legacy
 *  `buildInitialFilterModel` fallback). Sort/page/pageSize/multiSearchQuery
 *  on their own are not enough — they coexist with default chip filters. */
const CHIP_KEYS = [
  'serviceStatusName',
  'serviceTypeName',
  'contractName',
  'clientName',
  'clientCode',
  'contractLegacy',
  'serviceId',
  'af',
] as const;

export interface ServiceListUrlState {
  /** Filter items derived from codec-owned chip URL keys, with
   *  `serviceStatusGroup` expanded into explicit status names when no
   *  explicit chip values were provided. Empty when no chip keys are set. */
  filterItems: GridFilterItem[];
  /** True when at least one chip URL key is present. Caller uses this to
   *  decide whether to short-circuit the legacy `buildInitialFilterModel`
   *  fallback. Note: presence of just `multiSearchQuery`/`sort`/`page`
   *  does NOT count — those layer on top of legacy default filters. */
  hasChipState: boolean;
  multiSearchQuery: string;
  sort: SortToken | null;
  page: number | null;
  pageSize: number | null;
}

/**
 * Decode codec-owned URL params into a list-state snapshot. Caller resolves
 * the returned `filterItems` against legacy deep-links + open-status defaults.
 */
export const parseServiceListUrl = (searchParams: URLSearchParams): ServiceListUrlState => {
  const filterState: FilterState = {
    statuses: parseStringListParam(searchParams, 'serviceStatusName'),
    jobTypes: parseStringListParam(searchParams, 'serviceTypeName'),
    // Modern URL format: clientName holds client display names (unique per client).
    contracts: parseStringListParam(searchParams, 'clientName'),
    // Contract values have always been names on the wire, so unlike the client
    // slot there is no legacy code/hierarchy variant to round-trip here.
    contractNames: parseStringListParam(searchParams, 'contractName'),
    extraText: '',
    jobId: searchParams.get('serviceId') ?? '',
    multiSearch: '',
    advancedFilters: parseAdvancedFilters(searchParams),
  };

  // Legacy URL format: clientCode was used before the dropdown switched to
  // name-based values. Tag each value so buildFilterModel re-emits the original
  // clientCode predicate and old bookmarks/deep-links keep returning the same rows.
  // Single clientCode → 'equals'; multiple → 'isAnyOf' (matches buildFilterModel output shape).
  const legacyCodes = parseStringListParam(searchParams, 'clientCode');
  if (legacyCodes.length > 0) {
    const legacyOp = legacyCodes.length === 1 ? 'equals' : 'isAnyOf';
    filterState.contractsLegacyCode ??= {};
    for (const code of legacyCodes) {
      filterState.contracts.push(code);
      filterState.contractsLegacyCode[code] = legacyOp;
    }
  }

  for (const raw of searchParams.getAll('contractLegacy')) {
    const sep = raw.lastIndexOf('|');
    if (sep <= 0) continue;
    const value = raw.slice(0, sep);
    const operator = raw.slice(sep + 1);
    if (!value || !operator) continue;
    filterState.contracts.push(value);
    filterState.contractsLegacyHierarchy ??= {};
    filterState.contractsLegacyHierarchy[value] = operator;
  }

  // Activity-monitor 3-tuple: when the user lands via
  // `?clientCode=…&serviceTypeName=…&serviceStatusGroup=…` we still need to
  // expand the group into status names even though the codec has already
  // claimed clientCode/serviceTypeName as chip keys. Only expand when no
  // explicit serviceStatusName chips were supplied so re-mounts post-
  // writeback (which has expanded values) don't double up.
  const serviceStatusGroup = searchParams.get('serviceStatusGroup');
  if (serviceStatusGroup && filterState.statuses.length === 0) {
    const expanded = getServiceStatusNamesByStatusGroup(serviceStatusGroup)
      .split(',')
      .filter(Boolean);
    filterState.statuses.push(...expanded);
  }

  const filterItems = buildFilterModel(filterState).items;
  const hasChipState = CHIP_KEYS.some((k) => searchParams.has(k)) || !!serviceStatusGroup;

  return {
    filterItems,
    hasChipState,
    multiSearchQuery: searchParams.get('multiSearchQuery') ?? '',
    sort: parseSortParam(searchParams),
    page: searchParams.has('page') ? parseNumberParam(searchParams, 'page', 0, { min: 0 }) : null,
    pageSize: searchParams.has('pageSize')
      ? parseNumberParam(searchParams, 'pageSize', 25, { min: 1, max: MAX_PAGE_SIZE })
      : null,
  };
};

export interface ServiceListUrlSnapshot {
  filterModel: { items: GridFilterItem[] };
  multiSearchQuery: string;
  sort: SortToken | null;
  page: number;
  pageSize: number;
}

/**
 * Mutate `params` in place to match the given list-state snapshot. Clears
 * codec-owned and one-shot transient keys, then re-emits non-default
 * values. Defaults (page=0, pageSize=25, sort matching `defaultSort`) are
 * omitted to keep shared URLs short.
 */
export const writeServiceListUrl = (
  params: URLSearchParams,
  snapshot: ServiceListUrlSnapshot,
  defaultSort: SortToken
): void => {
  for (const key of OWNED_KEYS) params.delete(key);
  for (const key of TRANSIENT_KEYS) params.delete(key);

  // Skip filterModel items whose field names live in URL as their own
  // sticky scoping params — re-emitting them as chips would double up.
  const items = snapshot.filterModel.items.filter(
    (i) =>
      i.field !== 'locationId' && i.field !== 'importSessionId' && i.field !== 'structureNodeId'
  );
  const state = filterModelToFilterState({ items });

  setStringListParam(params, 'serviceStatusName', state.statuses);
  setStringListParam(params, 'serviceTypeName', state.jobTypes);
  setStringListParam(params, 'contractName', state.contractNames);

  // Split `contracts` into three buckets:
  //   modern       → clientName URL param (name-based, new format)
  //   legacyCode   → clientCode URL param (code-based, pre-name-switch reports/bookmarks)
  //   legacyHier   → contractLegacy URL param (pre-rebuild clientHierarchy predicates)
  const hierarchyMap = state.contractsLegacyHierarchy ?? {};
  const codeMap = state.contractsLegacyCode ?? {};
  const modern: string[] = [];
  const legacyCodes: string[] = [];
  const legacyHier: string[] = [];
  for (const v of state.contracts) {
    if (hierarchyMap[v]) legacyHier.push(`${v}|${hierarchyMap[v]}`);
    else if (codeMap[v]) legacyCodes.push(v);
    else modern.push(v);
  }
  setStringListParam(params, 'clientName', modern);
  setStringListParam(params, 'clientCode', legacyCodes);
  setStringListParam(params, 'contractLegacy', legacyHier);

  setOptionalParam(params, 'serviceId', state.jobId);
  setStringListParam(params, 'af', state.advancedFilters.map(serializeAdvancedFilter));

  setOptionalParam(params, 'multiSearchQuery', snapshot.multiSearchQuery);

  const isDefaultSort =
    snapshot.sort !== null &&
    snapshot.sort.field === defaultSort.field &&
    snapshot.sort.direction === defaultSort.direction;
  setSortParam(params, isDefaultSort ? null : snapshot.sort);

  if (snapshot.page > 0) params.set('page', String(snapshot.page));
  else params.delete('page');

  if (snapshot.pageSize !== 25) params.set('pageSize', String(snapshot.pageSize));
  else params.delete('pageSize');
};
